import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, ClipboardList, CreditCard, MessageSquare, TrendingUp, LogOut, Shield, RefreshCw, Mail, Check, X, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import { API, useAuth } from '../context/AuthContext';

const TABS = [
  { id: 'overview', label: 'Overview', icon: TrendingUp },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'quizzes', label: 'Quizzes', icon: ClipboardList },
  { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
];

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [subs, setSubs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');

  // Redirect if not logged in or not admin
  useEffect(() => {
    if (!loading) {
      if (!user) navigate('/login?next=/admin', { replace: true });
      else if (!user.is_admin) navigate('/dashboard', { replace: true });
    }
  }, [loading, user, navigate]);

  const load = async () => {
    setBusy(true);
    setError('');
    try {
      const [s, u, q, sub, m] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/quizzes`, { withCredentials: true }),
        axios.get(`${API}/admin/subscriptions`, { withCredentials: true }),
        axios.get(`${API}/admin/messages`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setQuizzes(q.data);
      setSubs(sub.data);
      setMessages(m.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const markRead = async (msgId) => {
    try {
      await axios.post(`${API}/admin/messages/${msgId}/mark-read`, {}, { withCredentials: true });
      setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, status: 'read' } : m)));
    } catch (e) {}
  };

  if (loading || !user || !user.is_admin) {
    return (
      <div className="min-h-screen bg-[#0d0e10] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#1f2024] loader-ring rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0e10] text-white">
      <Header />
      <main className="px-4 md:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header row */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-sm bg-[#ff4e15]/15 border border-[#ff4e15]/40 flex items-center justify-center">
                <Shield size={18} className="text-[#ff4e15]" />
              </span>
              <div>
                <div className="text-xs text-white/50 uppercase tracking-wide">Admin panel</div>
                <div className="heading-font text-2xl font-black">MYHEALTHYFY CONTROL CENTER</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={load}
                disabled={busy}
                className="flex items-center gap-2 px-3 py-2 border border-[#2a2b2f] rounded-sm text-sm hover:border-[#ff4e15] hover:text-[#ff4e15] transition-colors disabled:opacity-50"
              >
                <RefreshCw size={14} className={busy ? 'animate-spin' : ''} /> Refresh
              </button>
              <button
                onClick={async () => { await logout(); navigate('/'); }}
                className="flex items-center gap-2 px-3 py-2 border border-[#2a2b2f] rounded-sm text-sm hover:border-[#ff4e15] hover:text-[#ff4e15] transition-colors"
              >
                <LogOut size={14} /> Log out
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-[#1f1210] border border-[#ff4e15]/40 rounded-sm text-sm text-[#ff4e15]">
              {error}
            </div>
          )}

          {/* Tabs */}
          <div className="mt-6 border-b border-[#2a2b2f] flex flex-wrap gap-1">
            {TABS.map((t) => {
              const Ic = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors ${
                    active ? 'border-[#ff4e15] text-[#ff4e15]' : 'border-transparent text-white/60 hover:text-white'
                  }`}
                >
                  <Ic size={14} /> {t.label}
                  {t.id === 'messages' && stats?.messages?.new > 0 && (
                    <span className="ml-1 text-[10px] bg-[#ff4e15] text-white px-1.5 rounded-full font-bold">
                      {stats.messages.new}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="mt-6">
            {tab === 'overview' && (
              <Overview stats={stats} />
            )}
            {tab === 'users' && (
              <UsersTable
                users={users}
                onToggle={async (u, paid, planId) => {
                  try {
                    await axios.post(
                      `${API}/admin/users/${u.user_id}/set-paid`,
                      { paid, plan_id: planId || '3m' },
                      { withCredentials: true }
                    );
                    await load();
                  } catch (e) {
                    setError(e?.response?.data?.detail || 'Failed to update subscription');
                  }
                }}
              />
            )}
            {tab === 'quizzes' && (
              <DataTable
                data={quizzes}
                columns={[
                  { key: 'name', label: 'User' },
                  { key: 'email', label: 'Email' },
                  { key: 'answers', label: 'Gender', render: (a) => a?.gender || '—' },
                  { key: 'answers', label: 'Goal', render: (a) => {
                    const w = parseFloat(a?.measurements?.weight);
                    const t = parseFloat(a?.targetWeight || a?.['target-weight']);
                    if (w && t) return t > w ? 'Gain' : 'Loss';
                    return '—';
                  }},
                  { key: 'answers', label: 'Weight', render: (a) => {
                    const w = a?.measurements?.weight;
                    const u = a?.measurements?.weightUnit || 'lbs';
                    return w ? `${w} ${u}` : '—';
                  }},
                  { key: 'created_at', label: 'Taken', render: fmtDate },
                ]}
                emptyText="No quiz results yet"
              />
            )}
            {tab === 'subscriptions' && (
              <DataTable
                data={subs}
                columns={[
                  { key: 'name', label: 'User' },
                  { key: 'email', label: 'Email' },
                  { key: 'plan_name', label: 'Plan' },
                  { key: 'amount', label: 'Amount', render: (v) => v ? `₹${(v/100).toFixed(0)}` : '—' },
                  { key: 'status', label: 'Status', render: (v) => (
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${v === 'active' ? 'bg-[#ff4e15]/20 text-[#ff4e15]' : 'bg-[#2a2b2f] text-white/60'}`}>
                      {v}
                    </span>
                  )},
                  { key: 'mock', label: 'Mode', render: (v) => v ? 'MOCK' : 'REAL' },
                  { key: 'created_at', label: 'Purchased', render: fmtDate },
                ]}
                emptyText="No subscriptions yet"
              />
            )}
            {tab === 'messages' && (
              <MessagesList messages={messages} onMarkRead={markRead} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const Overview = ({ stats }) => {
  if (!stats) {
    return <div className="text-white/50">Loading stats…</div>;
  }
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total users" value={stats.users.total} sub={`+${stats.users.last_24h} in 24h`} />
        <StatCard label="Active subs" value={stats.subscriptions.active} sub={`${stats.subscriptions.total} total`} />
        <StatCard label="Revenue" value={`₹${stats.revenue_inr.toLocaleString('en-IN')}`} sub="from active subs" />
        <StatCard label="New messages" value={stats.messages.new} sub={`${stats.messages.total} total`} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Users (7d)" value={stats.users.last_7d} />
        <StatCard label="Quiz results" value={stats.quizzes.total} />
        <StatCard label="Users (24h)" value={stats.users.last_24h} />
        <StatCard label="Total subs" value={stats.subscriptions.total} />
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub }) => (
  <div className="p-4 bg-[#1a1b1e] border border-[#2a2b2f] rounded-sm">
    <div className="text-xs text-white/50 uppercase tracking-wide">{label}</div>
    <div className="heading-font text-3xl font-black text-white mt-1">{value}</div>
    {sub && <div className="text-[10px] text-[#ff4e15] mt-1">{sub}</div>}
  </div>
);

const UsersTable = ({ users, onToggle }) => {
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [planChoice, setPlanChoice] = useState({});

  const handleToggle = async (u, paid) => {
    setBusyId(u.user_id);
    const plan = planChoice[u.user_id] || '3m';
    await onToggle(u, paid, plan);
    setBusyId(null);
    setExpandedId(null);
  };

  if (!users || users.length === 0) {
    return <div className="p-8 text-center text-white/50 bg-[#1a1b1e] border border-[#2a2b2f] rounded-sm">No users yet</div>;
  }

  return (
    <div className="bg-[#1a1b1e] border border-[#2a2b2f] rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#2a2b2f]">
            <th className="text-left px-4 py-3 text-xs text-white/50 uppercase tracking-wide font-semibold">User</th>
            <th className="text-left px-4 py-3 text-xs text-white/50 uppercase tracking-wide font-semibold">Provider</th>
            <th className="text-left px-4 py-3 text-xs text-white/50 uppercase tracking-wide font-semibold">Subscription</th>
            <th className="text-left px-4 py-3 text-xs text-white/50 uppercase tracking-wide font-semibold">Status</th>
            <th className="text-left px-4 py-3 text-xs text-white/50 uppercase tracking-wide font-semibold">Joined</th>
            <th className="text-right px-4 py-3 text-xs text-white/50 uppercase tracking-wide font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const paid = !!u.is_paid;
            const sub = u.subscription;
            const isBusy = busyId === u.user_id;
            const isExpanded = expandedId === u.user_id;
            return (
              <React.Fragment key={u.user_id}>
                <tr className="border-b border-[#2a2b2f] last:border-b-0 hover:bg-[#141518]">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-white">{u.name || '—'}</div>
                    <div className="text-xs text-white/60">{u.email}</div>
                    {u.is_admin && (
                      <span className="inline-block mt-1 text-[10px] uppercase bg-[#ff4e15]/20 text-[#ff4e15] px-1.5 py-0.5 rounded-sm font-bold">Admin</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/70 capitalize">{u.auth_provider || 'password'}</td>
                  <td className="px-4 py-3">
                    {sub ? (
                      <div>
                        <div className="text-white font-medium">{sub.plan_name}</div>
                        <div className="text-xs text-white/50">
                          {sub.amount ? `₹${(sub.amount/100).toFixed(0)}` : '—'}
                          {sub.expires_at && ` · exp ${fmtDate(sub.expires_at)}`}
                        </div>
                      </div>
                    ) : (
                      <span className="text-white/40">No plan</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {paid ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-[#ff4e15]/20 text-[#ff4e15] px-2 py-1 rounded-sm">
                        <Check size={11} /> Paid
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-[#2a2b2f] text-white/60 px-2 py-1 rounded-sm">
                        <X size={11} /> Unpaid
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/70">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                      {u.auth_provider !== 'google' && (
                        <button
                          onClick={async () => {
                            const pw = window.prompt(`Enter a new password for ${u.email} (min 6 chars):`);
                            if (!pw) return;
                            if (pw.length < 6) { alert('Password must be at least 6 characters'); return; }
                            try {
                              await axios.post(`${API}/admin/users/${u.user_id}/reset-password`, { new_password: pw }, { withCredentials: true });
                              alert(`Password reset for ${u.email}. They can now sign in with the new password.`);
                            } catch (e) {
                              alert(e?.response?.data?.detail || 'Failed to reset password');
                            }
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-[#2a2b2f] rounded-sm text-white/70 hover:border-[#ff4e15] hover:text-[#ff4e15] transition-colors"
                          title="Reset user password"
                        >
                          🔑 Reset pw
                        </button>
                      )}
                      {paid ? (
                        <button
                          onClick={() => handleToggle(u, false)}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[#2a2b2f] rounded-sm text-white/70 hover:border-[#ff4e15] hover:text-[#ff4e15] disabled:opacity-50 transition-colors"
                        >
                          {isBusy ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                          Mark unpaid
                        </button>
                      ) : (
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : u.user_id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#ff4e15] text-white rounded-sm hover:bg-[#ff6033] transition-colors"
                        >
                          <Check size={12} /> Mark paid
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {isExpanded && !paid && (
                  <tr className="bg-[#0d0e10] border-b border-[#2a2b2f]">
                    <td colSpan={6} className="px-4 py-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs text-white/60">Grant plan:</span>
                        <div className="inline-flex bg-[#141518] border border-[#2a2b2f] rounded-sm p-1">
                          {[
                            { id: '1m', label: '1-Month · ₹999' },
                            { id: '3m', label: '3-Month · ₹1,999' },
                            { id: '6m', label: '6-Month · ₹2,999' },
                          ].map((p) => {
                            const chosen = (planChoice[u.user_id] || '3m') === p.id;
                            return (
                              <button
                                key={p.id}
                                onClick={() => setPlanChoice((prev) => ({ ...prev, [u.user_id]: p.id }))}
                                className={`px-2.5 py-1 text-xs font-semibold rounded-sm transition-colors ${
                                  chosen ? 'bg-[#ff4e15] text-white' : 'text-white/60 hover:text-white'
                                }`}
                              >
                                {p.label}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          onClick={() => handleToggle(u, true)}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#ff4e15] text-white rounded-sm hover:bg-[#ff6033] disabled:opacity-50 transition-colors"
                        >
                          {isBusy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                          Confirm
                        </button>
                        <button
                          onClick={() => setExpandedId(null)}
                          className="text-xs text-white/50 hover:text-white/80"
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};


const DataTable = ({ data, columns, emptyText }) => {
  if (!data || data.length === 0) {
    return <div className="p-8 text-center text-white/50 bg-[#1a1b1e] border border-[#2a2b2f] rounded-sm">{emptyText}</div>;
  }
  return (
    <div className="bg-[#1a1b1e] border border-[#2a2b2f] rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#2a2b2f]">
            {columns.map((c, i) => (
              <th key={i} className="text-left px-4 py-3 text-xs text-white/50 uppercase tracking-wide font-semibold">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} className="border-b border-[#2a2b2f] last:border-b-0 hover:bg-[#141518]">
              {columns.map((c, j) => {
                const raw = row[c.key];
                return (
                  <td key={j} className="px-4 py-3 text-white/90">
                    {c.render ? c.render(raw, row) : (raw ?? '—')}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MessagesList = ({ messages, onMarkRead }) => {
  if (!messages || messages.length === 0) {
    return <div className="p-8 text-center text-white/50 bg-[#1a1b1e] border border-[#2a2b2f] rounded-sm">No messages yet</div>;
  }
  return (
    <div className="space-y-3">
      {messages.map((m) => (
        <div key={m.id} className={`p-4 rounded-sm border ${m.status === 'new' ? 'bg-[#1f1210] border-[#ff4e15]/40' : 'bg-[#1a1b1e] border-[#2a2b2f]'}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-white">{m.name}</span>
                <a href={`mailto:${m.email}`} className="text-xs text-[#ff4e15] hover:underline flex items-center gap-1">
                  <Mail size={11} /> {m.email}
                </a>
                <span className="text-[10px] uppercase bg-[#0d0e10] border border-[#2a2b2f] px-2 py-0.5 rounded-sm text-white/60">
                  {m.subject}
                </span>
                {m.status === 'new' && (
                  <span className="text-[10px] uppercase bg-[#ff4e15] text-white px-2 py-0.5 rounded-sm font-bold">NEW</span>
                )}
              </div>
              <div className="mt-2 text-sm text-white/80 whitespace-pre-wrap">{m.message}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] text-white/50">{fmtDate(m.created_at)}</div>
              {m.status === 'new' && (
                <button
                  onClick={() => onMarkRead(m.id)}
                  className="mt-2 text-xs flex items-center gap-1 text-white/60 hover:text-[#ff4e15] transition-colors"
                >
                  <Check size={12} /> Mark read
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default AdminPanel;
